import { useQuery, useMutation, type UseQueryResult } from '@tanstack/react-query'
import { apiFetch } from './client'
import type { ApiError } from './types'

export interface NotificationStatus {
  /** False when the server has no VAPID keys — the UI offers nothing to turn on. */
  configured: boolean
  active_subscriptions: number
}

export interface PushSubscriptionPayload {
  endpoint: string
  keys: { p256dh: string; auth: string }
  user_agent?: string
  timezone?: string
}

export interface TestNotificationResult {
  delivered: number
  failed: number
}

export function fetchVapidPublicKey(): Promise<{ public_key: string }> {
  return apiFetch<{ public_key: string }>('/notifications/vapid-public-key')
}

export function registerPushSubscription(body: PushSubscriptionPayload): Promise<{ id: string }> {
  return apiFetch<{ id: string }>('/notifications/subscriptions', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function unregisterPushSubscription(endpoint: string): Promise<void> {
  return apiFetch<void>('/notifications/subscriptions', {
    method: 'DELETE',
    body: JSON.stringify({ endpoint }),
  })
}

export function fetchNotificationStatus(): Promise<NotificationStatus> {
  return apiFetch<NotificationStatus>('/notifications/status')
}

export function sendTestNotification(): Promise<TestNotificationResult> {
  return apiFetch<TestNotificationResult>('/notifications/test', { method: 'POST' })
}

export function useNotificationStatus(): UseQueryResult<NotificationStatus, ApiError> {
  return useQuery({
    queryKey: ['notifications', 'status'],
    queryFn: fetchNotificationStatus,
  })
}

export function useSendTestNotification() {
  return useMutation<TestNotificationResult, ApiError, void>({
    mutationFn: () => sendTestNotification(),
  })
}
